import { useState, useEffect } from "react";

import { useFormik } from "formik";
import dayjs from "dayjs";

import Col from "react-bootstrap/Col";

import { useLayout } from "@/../_metronic/layout/core";
import { useDebounce } from "@/hooks/useDebounce";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { exportExcel } from "@/utils/exports";
import { DiagnosticHypothesis } from "@/@types/sintegrada/sintegradaServerApi/DiagnosticHypothesis";
import { DiagnosticHypothesisGroupByProfessional } from "@/@types/sintegrada/sintegradaServerApi/DiagnosticHypothesisGroupByProfessional";
import { PaginatedDiagnosticHypothesis } from "@/@types/sintegrada/sintegradaServerApi/PaginatedDiagnosticHypothesis";
import { PaginatedDiagnosticHypothesisGroupByProfessional } from "@/@types/sintegrada/sintegradaServerApi/PaginatedDiagnosticHypothesisGroupByProfessional";
import {
	diagnosticHypothesisFilterInitialValues,
	TypeofDiagnosticHypothesisFilterInitialValues,
} from "@/utils/initialValues/diagnosticHypothesisFilter";
import { diagnosticHypothesisFilterSchema } from "@/utils/Schemas/diagnosticHypothesisFilterSchema";
import { getDiagnosticHypothesis } from "@/services/sintegradaserver/diagnosticHypothesis/getDiagnosticHypothesis";
import { formatPatientAddress } from "@/utils/formatPatientAddress";

import { LoadingScreen } from "@/components/LoadingScreen";
import { SearchInput } from "@/components/SearchInput";
import { DiagnosticHypothesisFilter } from "./DiagnosticHypothesisFilter";
import { DiagnosticHypothesisTable } from "./DiagnosticHypothesisTable";
import { PaginationLinks } from "@/components/PaginationLinks";
import { DiagnosticHypothesisGroupByProfessionalTable } from "./DiagnosticHypothesisGroupByProfessionalTable";
import { ReportBaseLayout } from "@/components/sintegrada/ReportBaseLayout";

export function DiagnosticHypothesisReport() {
	const { setTitle } = useLayout();
	const { cnes, ibgeCode } = useCurrentAccount();

	const [isLoading, setIsLoading] = useState(false);
	const [isLoadingExportExcel, setIsLoadingExportExcel] = useState(false);

	const [diagnosticHypothesis, setDiagnosticHypothesis] = useState<PaginatedDiagnosticHypothesis>(
		{} as PaginatedDiagnosticHypothesis
	);
	const [diagnosticHypothesisGroupByProfessional, setDiagnosticHypothesisGroupByProfessional] =
		useState({} as PaginatedDiagnosticHypothesisGroupByProfessional);

	const [page, setPage] = useState(1);

	const [search, setSearch] = useState("");
	const debouncedSearch = useDebounce(search, 2000);

	const diagnosticHypothesisFilterValues = getSessionStorageDiagnosticHypothesisFilterValues();

	const hasDiagnosticHypothesisFilterValues = Boolean(
		Object.keys(diagnosticHypothesisFilterValues).length
	);

	const hasDiagnosticHypothesisResults = Boolean(diagnosticHypothesis?.data?.length);
	const hasDiagnosticHypothesisGroupByProfessionalResults = Boolean(
		diagnosticHypothesisGroupByProfessional?.data?.length
	);

	const disabledExportButton =
		(diagnosticHypothesisFilterValues.groupByProfessional &&
			!hasDiagnosticHypothesisGroupByProfessionalResults) ||
		(!diagnosticHypothesisFilterValues.groupByProfessional && !hasDiagnosticHypothesisResults);

	function getSessionStorageDiagnosticHypothesisFilterValues() {
		const diagnosticHypothesisFilterData =
			sessionStorage.getItem("diagnosticHypothesisValues") || "{}";
		const diagnosticHypothesisFilterDataJSON = JSON.parse(diagnosticHypothesisFilterData);

		return diagnosticHypothesisFilterDataJSON as TypeofDiagnosticHypothesisFilterInitialValues;
	}

	function transformParams(values: TypeofDiagnosticHypothesisFilterInitialValues) {
		const groupBy = values.groupByProfessional ? "group_by_professional" : "default";

		const params = {
			cnes,
			ibgeCode,
			startDate: values.startDate,
			endDate: values.endDate,
			sexo: values.gender.value,
			icd_code: values.icd?.value,
			minAge: values.minAge,
			maxAge: values.maxAge,
			riskClassificationId: values.riskClassification?.value?.id,
			professionalId: values.professional?.value?.id,
			specialty_id: values.clinicOrSpecialty?.value?.specialty.id,
			department: values.clinicOrSpecialty?.value?.department.id,
			group_by: groupBy,
		};

		return params;
	}

	async function handleGetDiagnosticHypothesisTableData(page = 1, search = "") {
		setIsLoading(true);

		const transformedParams = transformParams(diagnosticHypothesisFilterValues);

		const data = await getDiagnosticHypothesis({
			page,
			search,
			...transformedParams,
		});

		if (diagnosticHypothesisFilterValues.groupByProfessional) {
			setDiagnosticHypothesisGroupByProfessional(
				data as PaginatedDiagnosticHypothesisGroupByProfessional
			);

			setIsLoading(false);

			return;
		}

		setDiagnosticHypothesis(data as PaginatedDiagnosticHypothesis);

		setIsLoading(false);
	}

	const formik = useFormik({
		initialValues: diagnosticHypothesisFilterInitialValues,
		validationSchema: diagnosticHypothesisFilterSchema,
		async onSubmit(values) {
			const transformedParams = transformParams(values);

			const data = await getDiagnosticHypothesis({
				page: 1,
				...transformedParams,
			});

			if (values.groupByProfessional) {
				setDiagnosticHypothesisGroupByProfessional(
					data as PaginatedDiagnosticHypothesisGroupByProfessional
				);

				sessionStorage.setItem("diagnosticHypothesisValues", JSON.stringify(values));

				return;
			}

			setDiagnosticHypothesis(data as PaginatedDiagnosticHypothesis);

			sessionStorage.setItem("diagnosticHypothesisValues", JSON.stringify(values));
		},
	});

	function transformDiagnosticHypothesisTableToExportExcel(results: DiagnosticHypothesis[]) {
		return results.map((result) => {
			const { patient } = result;
			
			const attendanceStartDate = patient.attendance_start
				? dayjs(patient.attendance_start).format("DD/MM/YYYY HH:mm")
				: "N/A";

			const birthdate = patient.birthday
				? dayjs(patient.birthday).format("DD/MM/YYYY")
				: "N/A";

			const icd = `${result.icd_code || "N/A"} - ${result.icd_description || "N/A"}`;

			const address = formatPatientAddress(
				patient.street,
				patient.house_number,
				patient.neighborhood,
				patient.city,
				patient.state,
				patient.zip_code,
			);

			return {
				"Dt. do Atendimento": attendanceStartDate,
				"Nome do paciente": patient.name || "N/A",
				"Dt. Nascimento": birthdate,
				Sexo: result.patient.sex || "N/A",
				Endereço: address,
				Classificação: result.risk.name || "N/A",
				Profissional: result.professional_name || "N/A",
				CID: icd,
			};
		});
	}

	function transformDiagnosticHypothesisGroupByProfessionalTableToExportExcel(
		results: DiagnosticHypothesisGroupByProfessional[]
	) {
		return results.map((result) => ({
			Profissional: result.professional_name || "N/A",
			CID: result.icd_description || "N/A",
			Total: result.total || 0,
		}));
	}

	async function handleExportExcel() {
		setIsLoadingExportExcel(true);

		const params = transformParams(diagnosticHypothesisFilterValues);

		const data = await getDiagnosticHypothesis({
			...params,
			all_results: 1,
		});

		if (diagnosticHypothesisFilterValues.groupByProfessional) {
			const transformedData =
				transformDiagnosticHypothesisGroupByProfessionalTableToExportExcel(
					(data as PaginatedDiagnosticHypothesisGroupByProfessional).data
				);

			exportExcel(
				transformedData,
				"Relatório de hipótese diagnóstica agrupado por profissional"
			);

			setIsLoadingExportExcel(false);

			return;
		}

		const transformedData = transformDiagnosticHypothesisTableToExportExcel(
			(data as PaginatedDiagnosticHypothesis).data
		);

		exportExcel(transformedData, "Relatório de hipótese diagnóstica");

		setIsLoadingExportExcel(false);
	}

	async function handleChangePage(page: number) {
		setPage(page);
	}

	useEffect(() => {
		if (hasDiagnosticHypothesisResults || hasDiagnosticHypothesisGroupByProfessionalResults) {
			handleGetDiagnosticHypothesisTableData(page);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page]);

	useEffect(() => {
		if (hasDiagnosticHypothesisResults || hasDiagnosticHypothesisGroupByProfessionalResults) {
			handleGetDiagnosticHypothesisTableData(1, search);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedSearch]);

	useEffect(() => {
		setTitle("Relatório de hipótese diagnóstica");
		sessionStorage.removeItem("diagnosticHypothesisValues");

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<ReportBaseLayout>
			<LoadingScreen loading={formik.isSubmitting || isLoadingExportExcel} />

			<DiagnosticHypothesisFilter
				formik={formik}
				handleExportExcel={handleExportExcel}
				disabledExportButton={disabledExportButton}
			/>

			{hasDiagnosticHypothesisFilterValues &&
				(diagnosticHypothesisFilterValues.groupByProfessional ? (
					<>
						<DiagnosticHypothesisGroupByProfessionalTable
							isLoading={isLoading}
							results={diagnosticHypothesisGroupByProfessional?.data}
						/>

						{hasDiagnosticHypothesisGroupByProfessionalResults && (
							<PaginationLinks
								itemsPerPage={
									diagnosticHypothesisGroupByProfessional.meta
										.per_page
								}
								pageNumber={
									diagnosticHypothesisGroupByProfessional.meta
										.current_page
								}
								totalPages={
									diagnosticHypothesisGroupByProfessional.meta.total
								}
								changeSelectedPage={handleChangePage}
							/>
						)}
					</>
				) : (
					<>
						<Col lg={12} className="d-flex justify-content-end mb-8">
							<SearchInput
								placeholder="Buscar por nome ou dt. nasc"
								value={search}
								setValue={setSearch}
							/>
						</Col>

						<DiagnosticHypothesisTable
							isLoading={isLoading}
							results={diagnosticHypothesis?.data}
						/>

						{hasDiagnosticHypothesisResults && (
							<PaginationLinks
								itemsPerPage={diagnosticHypothesis.meta.per_page}
								pageNumber={diagnosticHypothesis.meta.current_page}
								totalPages={diagnosticHypothesis.meta.total}
								changeSelectedPage={handleChangePage}
							/>
						)}
					</>
				))}
		</ReportBaseLayout>
	);
}